
import { deleteDB, DBSchema, IDBPDatabase, StoreNames, StoreKey } from 'idb'
import { openDB } from 'idb/with-async-ittr'
import { isObject } from 'lodash-es'
import { wrap } from './index'

import { MissingPrimaryKeyError } from '../errors'

export function fixIDB() {
  if (!window.indexedDB) window.indexedDB = window.indexedDB || window['mozIndexedDB' as never] || window['webkitIndexedDB' as never] || window['msIndexedDB' as never]
  if (!window.IDBTransaction) window.IDBTransaction = window.IDBTransaction || window['webkitIDBTransaction' as never] || window['msIDBTransaction' as never]
  if (!window.IDBKeyRange) window.IDBKeyRange = window.IDBKeyRange || window['webkitIDBKeyRange' as never] || window['msIDBKeyRange' as never]
}

export type StorageConfig = {
  permanent: boolean
}

const enum TRANSACTION_TYPE {
  READWRITE = 'readwrite',
  READ = 'readonly'
}

type Index<T> = {
  name: string
  key: string
  options?: IDBIndexParameters,
  value?: T
}

type Store<T> = {
  name: StoreNames<T>
  options: IDBObjectStoreParameters,
  indexes?: Index<unknown>[]
}

type StorageSchema = DBSchema

export { StorageSchema, Store, Index }

export interface ActiveRecord {
  readonly name: string
  find<T, P>(key: P): Promise<T>
  remove<T, I>(key: T | Array<T>, index?: Index<I>): Promise<void>
  save<T, DB>(data: T, key?: string | number | IDBKeyRange | StoreKey<DB, StoreNames<DB>>): Promise<void>
  all<T>(): Promise<Array<T>>
  clear(): Promise<void>
}

export type AsyncStorage<SCHEMA extends StorageSchema> = {
  stores: Record<StoreNames<string>, ActiveRecord>
  db?: IDBPDatabase<SCHEMA> | null
  clear(): Promise<void>
}

interface AbstractStorage extends Storage {
  getItem<T>(key: string): T
}

class DataStorage implements AbstractStorage {
  #config: StorageConfig
  #storage: Storage

  get config(): StorageConfig {
    return this.#config
  }

  get storage(): Storage {
    return this.#storage
  }

  get length() {
    return this.#storage.length || 0
  }

  constructor(config: StorageConfig = { permanent: true }) {
    this.#config = config
    this.#storage = createStorage(config)
  }

  key(index: number) {
    return this.storage.key(index)
  }

  clear() {
    return this.storage.clear()
  }

  getItem<T>(key: string): T {
    try {
      const value = this.storage.getItem(key)
      if (value != null) {
        return JSON.parse(value) as T
      } else return undefined as unknown as T
    } catch {
      return undefined as unknown as T
    }
  }

  setItem<T>(key: string, value: T) {
    this.storage.setItem(key, JSON.stringify(value))
  }

  removeItem(key: string) {
    return this.storage.removeItem(key)
  }
}

export function createStorage(config: StorageConfig) {
  try {
    const storage = config.permanent ? window['localStorage'] : window['sessionStorage']
    const key = Math.round(Math.random() * 100000) + '_tmp_key'
    storage[key] = key
    delete storage[key]

    return storage
  } catch {
    const storage = new Map

    return {
      get length() { return storage.size || 0 },
      getItem: <T>(key: string | number) => storage.get(key) as T,
      setItem: <T>(key: string | number, value: T) => storage.set(key, value),
      removeItem(key: string | number) { storage.delete(key) },
      key: (index: number) => Array.from(storage.keys())[index],
      clear: () => storage.clear()
    }
  }
}

const handler = {
  get<T>(target: DataStorage, key: keyof DataStorage & string): T {
    if ([
      'length', 'key', 'clear', 'storage', 'config',
      'getItem', 'setItem', 'removeItem'
    ].includes(key)) {
      return target[key] as T
    } else {
      return target.getItem(key) as T
    }
  },

  set<T>(target: DataStorage, key: string, value: T) {
    target.setItem(key, value)
    return true
  },

  deleteProperty(target: DataStorage, key: string) {
    target.removeItem(key)
    return true
  }
}

export const LocalStorage = new Proxy(new DataStorage, handler)
export const SessionStorage = new Proxy(new DataStorage({ permanent: false }), handler)

// *********************************************************************************** //

const bySameKey = <K>(key: K, pkey: string) => <R extends Record<string, unknown>>(record: R) => isObject(record) ? record[pkey] === key : record === key

export class SyncDataStorage implements ActiveRecord {
  #name: string
  #key: string

  get name() {
    return this.#name
  }

  get db() {
    return null
  }

  constructor(name: StoreNames<string>, primaryKey: string) {
    this.#name = name
    this.#key = primaryKey
    if (!primaryKey) throw new MissingPrimaryKeyError
  }

  find<T, K>(key: K) {
    const list = LocalStorage.getItem(this.name) as Array<T>
    return Promise.resolve(list.find(bySameKey(key, this.#key) as never) as T) //
  }

  remove<T>(key: T) {
    const list = LocalStorage.getItem(this.name) as Array<T>

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    LocalStorage.setItem(this.name, list.filter((record: any) =>
      Array.isArray(key) ?
        !key.includes(record[this.#key]) :
        record[this.#key] !== key))

    return Promise.resolve(undefined)
  }

  save<T>(data: T, key?: string | number) {
    const list = LocalStorage.getItem(this.name) as Array<T>

    if (Array.isArray(data)) {
      if (key) {
        const keys = data.map(i => i[this.#key])

        list.forEach((item, i) => {
          const index = keys.indexOf(item[this.#key as never])
          if (index >= 0) list[i] = data[index]
        })

        LocalStorage.setItem(this.name, list)
      } else {
        LocalStorage.setItem(this.name, [...list, ...data])
      }
    } else {
      const k = key || (isObject(data) ? data[this.#key as never] : data)
      const index = key != null ? list.findIndex(bySameKey(k, this.#key) as never) : -1

      if (index >= 0) {
        list[index] = data
      } else {
        list.push(data)
      }

      LocalStorage.setItem(this.name, list)
    }

    return Promise.resolve(undefined)
  }

  clear() {
    return Promise.resolve(LocalStorage.setItem(this.name, []))
  }

  all<T>() {
    return Promise.resolve((LocalStorage.getItem(this.name) || []) as Array<T>)
  }
}

export class AsyncDataStorage<DB> implements ActiveRecord {
  #db: IDBPDatabase<DB>
  #name: StoreNames<string>

  constructor(db: IDBPDatabase<DB>, name: StoreNames<string>) {
    this.#db = db
    this.#name = name
  }

  get name() {
    return this.#name
  }

  get db() {
    return this.#db
  }

  public find<T, P>(key: P, query?: string | IDBKeyRange) {
    return this.db.get(this.name as never, (query || key) as never) as Promise<T>
  }

  public async remove<T, Q>(key: T, query?: Index<Q>) {
    const { store, done } = this.db.transaction(this.name as never, TRANSACTION_TYPE.READWRITE)

    if (Array.isArray(key)) {
      const list = [...key]
      const isIndexSupported = IDBKeyRange && IDBIndex
      const isIndexExisting = store.indexNames && store.indexNames.contains(query?.name as never)

      if (query && isIndexSupported && isIndexExisting) {
        // Remove all by index
        const index = store.index(query.name as never)
        await butchRemove(index.iterate(query.value as never) as never, done, list)
      } else {
        // Remove all
        await butchRemove(store as never, done, list)
      }
    }
    else {
      const pkey = Array.isArray(store.keyPath) ? store.keyPath[0] || 'id' : store.keyPath
      const row = key && typeof key === 'object' ? key[pkey as never] : key

      await Promise.all([
        store.delete(row as never),
        done
      ])
    }
  }

  public async save<T, DB>(data: T, key?: IDBKeyRange | StoreKey<DB, StoreNames<DB>>) {
    const transaction = this.db.transaction(this.name as never, TRANSACTION_TYPE.READWRITE)
    const k = transaction.store.keyPath ? undefined : key as never

    if (Array.isArray(data)) {
      await Promise.all([
        data.map(item => transaction.store.put(item, k)),
        transaction.done
      ])
    } else {
      await Promise.all([
        transaction.store.put(data as never, k),
        transaction.done
      ])
    }
  }

  public async clear() {
    await this.db.clear(this.name as never)
  }

  public async all<T>() {
    return (this.db.getAll(this.name as never) || []) as Promise<T[]>
  }
}

function create<T extends StorageSchema>(name: string, stores: Store<string>[], version = 1, fallback: boolean) {
  fixIDB()

  const isIDB = window.IDBCursor && window.IDBDatabase && window.IDBFactory && window.IDBIndex && window.IDBTransaction && window.IDBKeyRange

  try {
    if (!isIDB || fallback)
      throw new Error('IndexeDB is not supported')

    return openDB(name, version, {
      upgrade(db: IDBPDatabase<T>, oldVersion: number, newVersion: number) {
        try {
          if (oldVersion < newVersion) {
            stores.forEach(store => {
              if (db) {
                if (db.objectStoreNames.contains(store.name as never)) db.deleteObjectStore(store.name as never)
                const s = db.createObjectStore(store.name as never, store.options);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (<any>db)['indexes'] = Array.isArray(store.indexes) ?
                  store.indexes.map(index => s.createIndex(index.name as never, index.key, index.options)) : []
              }
            })
          }
        } catch (error) {
          stores.forEach(store => {
            LocalStorage.removeItem(name + ':' + store.name)
            LocalStorage.setItem(name + ':' + store.name, [])
          })
          // console.warn('Storage upgrade failed!', error)
        }
      }
    })
  } catch (error) {

    stores.forEach(store => {
      const key = name + ':' + store.name
      const s = LocalStorage.getItem(key)
      if (!s) LocalStorage.setItem(key, [])
    })


    return window.indexedDB && wrap(deleteDB(name))
  }
}

/**
 * Create Indexed DB storge with fallback to LocalStorage with single api interface. Provide custom storge schema.
 */
export async function storage<SCHEMA extends StorageSchema>(name: string, stores: Store<string>[], version = 1.0, fallback = false) {
  const db = await wrap(create<SCHEMA>(name, stores, version, fallback)) as IDBPDatabase<SCHEMA>
  const map = {} as Record<StoreNames<string>, ActiveRecord>

  stores.forEach(store => map[store.name] = db ?
    new AsyncDataStorage(db, store.name) :
    new SyncDataStorage(name + ':' + store.name, store.options.keyPath as never)
  )

  return {
    stores: map,
    db,
    async clear() {
      if (db && !fallback) {
        const transactions = stores.map(store => db.transaction(store.name as never, TRANSACTION_TYPE.READWRITE))

        await Promise.all([
          ...transactions.map(transaction => transaction?.store.clear()),
          ...transactions.map(transaction => transaction?.done)
        ])
      } else {
        stores.forEach(store => map[store.name].clear())
      }
    }
  } as AsyncStorage<SCHEMA>
}

type Cursor<T> = {
  value: T
  primaryKey: string
  delete(): Promise<void>
}

async function butchRemove<T>(
  iterator: IterableIterator<Cursor<T>> | AsyncIterableIterator<Cursor<T>>,
  done: Promise<void>,
  list: Array<string | number>
) {
  for await (const cursor of iterator) {
    const i = list.findIndex(key => (cursor.primaryKey) === key)

    if (i >= 0 && cursor) {
      delete list[i]
      await cursor.delete()
    }
  }

  await done
}
